import moment from 'moment-timezone'
import { addAlert, isAuthorized } from '@/utilities/helpers'
import { getRecordsByDataProperty } from '@/services/reports-service'
import { findTransportationProfiles } from '@/services/transportation-profiles-service'

export default {
  name: 'home',
  props: {
    signedInUser: Object
  },
  data () {
    return {
      summaryLoading: false,
      summaryCards: [],
      vehicleNameById: {}
    }
  },
  computed: {
    getSignedInUser () {
      return this.$store.state.credentials.user
    }
  },
  mounted () {
    if (this.getSignedInUser) {
      this.loadDashboardSummary()
    }
  },
  watch: {
    getSignedInUser (updatedUser, oldUser) {
      if (updatedUser && (!oldUser || oldUser.id !== updatedUser.id)) {
        this.loadDashboardSummary()
      }
    }
  },
  methods: {
    afterLoginForward (signInResult) {
      if (this.$route.query.forward) {
        this.$router.push({ path: this.$route.query.forward })
      }
    },
    async loadDashboardSummary () {
      if (!this.getSignedInUser) {
        this.summaryCards = []
        return
      }

      this.summaryLoading = true
      this.summaryCards = []

      try {
        const scopedVehicleIds = await this.resolveScopedVehicleIds()
        const periodConfigs = this.buildSummaryPeriods()

        const periodResults = await Promise.all(periodConfigs.map(async (periodConfig) => {
          const records = await this.findRecordsForPeriod(periodConfig, scopedVehicleIds)
          return this.buildSummaryCard(periodConfig, records)
        }))

        this.summaryCards = periodResults
      } catch (err) {
        addAlert({
          message: err,
          type: 'error'
        })
      } finally {
        this.summaryLoading = false
      }
    },
    buildSummaryPeriods () {
      const dayStart = moment.utc().startOf('day').subtract(1, 'day')
      const dayEnd = moment.utc().startOf('day')

      const weekStart = moment.utc().startOf('isoWeek').subtract(1, 'week')
      const weekEnd = moment.utc().startOf('isoWeek')

      const monthStart = moment.utc().startOf('month').subtract(1, 'month')
      const monthEnd = moment.utc().startOf('month')

      const yearStart = moment.utc().startOf('year').subtract(1, 'year')
      const yearEnd = moment.utc().startOf('year')

      return [
        {
          key: 'day',
          title: `Yesterday - ${dayStart.format('ddd')}`,
          sourceType: 'source',
          reportFidelity: null,
          periodStartIso: dayStart.toISOString(),
          periodEndIso: dayEnd.toISOString()
        },
        {
          key: 'week',
          title: 'Last Week',
          sourceType: 'aggregate',
          reportFidelity: 'week',
          periodStartIso: weekStart.toISOString(),
          periodEndIso: weekEnd.toISOString()
        },
        {
          key: 'month',
          title: `Last Month - ${monthStart.format('MMM')}`,
          sourceType: 'aggregate',
          reportFidelity: 'month',
          periodStartIso: monthStart.toISOString(),
          periodEndIso: monthEnd.toISOString()
        },
        {
          key: 'year',
          title: `Last Year - ${yearStart.format('YYYY')}`,
          sourceType: 'aggregate',
          reportFidelity: 'year',
          periodStartIso: yearStart.toISOString(),
          periodEndIso: yearEnd.toISOString()
        }
      ]
    },
    async resolveScopedVehicleIds () {
      const user = this.getSignedInUser
      const isStaff = isAuthorized(user, 'staff')
      const isTransporter = isAuthorized(user, 'transporter')

      if (!isTransporter || isStaff) {
        return null
      }

      const transportationOwnerUsers = user && user.transportationOwnerUsers ? user.transportationOwnerUsers : []
      const ownerIds = transportationOwnerUsers
        .map((item) => item && item.transportationOwnerId)
        .filter((id) => !!id)

      if (!ownerIds.length) {
        return []
      }

      const ownerWhere = ownerIds.length === 1
        ? { transportationOwnerId: ownerIds[0] }
        : { transportationOwnerId: { inq: ownerIds } }

      const profiles = await findTransportationProfiles({
        fields: { id: true, licensePlateNumber: true },
        join: {
          relation: 'transportationOwnerProfile',
          scope: { where: ownerWhere }
        }
      })

      const ids = []
      for (let i = 0; i < profiles.length; i += 1) {
        const profile = profiles[i]
        if (profile && profile.id) {
          ids.push(profile.id)
          this.vehicleNameById[profile.id] = profile.licensePlateNumber || profile.id
        }
      }

      return ids
    },
    async findRecordsForPeriod (periodConfig, scopedVehicleIds) {
      if (Array.isArray(scopedVehicleIds) && !scopedVehicleIds.length) {
        return []
      }

      const scopedCategoryIds = Array.isArray(scopedVehicleIds) && scopedVehicleIds.length
        ? scopedVehicleIds
        : undefined

      const records = await getRecordsByDataProperty({
        target: periodConfig.sourceType === 'source' ? 'source' : 'aggregate',
        reportType: 'distance_travelled',
        sortOrder: 'DESC',
        propertyType: 'numeric',
        periodStart: periodConfig.periodStartIso,
        periodEnd: periodConfig.periodEndIso,
        dataProperty: 'distance',
        categoryIds: scopedCategoryIds,
        limit: 5,
        offset: 0
      })

      const safeRecords = Array.isArray(records) ? records : []

      return safeRecords.filter((record) => {
        const isCompleted = record && record.reportStatus === 'completed'
        const isTransportationCategory = record && record.category === 'transportation'

        if (!Array.isArray(scopedVehicleIds) || !scopedVehicleIds.length) {
          return isCompleted && isTransportationCategory
        }

        return isCompleted &&
          isTransportationCategory &&
          !!(record && record.categoryId) &&
          scopedVehicleIds.includes(String(record.categoryId))
      })
    },
    async buildSummaryCard (periodConfig, records) {
      const distanceByVehicleId = {}

      for (let i = 0; i < records.length; i += 1) {
        const record = records[i]
        const vehicleId = record && record.categoryId ? String(record.categoryId) : null
        if (!vehicleId) {
          continue
        }
        const distance = record && record.data && record.data.distance ? Number(record.data.distance) : 0
        if (!distanceByVehicleId[vehicleId]) {
          distanceByVehicleId[vehicleId] = 0
        }
        distanceByVehicleId[vehicleId] += Number.isFinite(distance) ? distance : 0
      }

      const ranking = Object.keys(distanceByVehicleId).map((vehicleId) => ({
        vehicleId: vehicleId,
        distance: Number(distanceByVehicleId[vehicleId] || 0)
      })).sort((a, b) => b.distance - a.distance)

      await this.ensureVehicleLabels(ranking.map((item) => item.vehicleId))

      const topRecord = ranking.length ? ranking[0] : null
      const rankedVehicles = ranking.slice(0, 5)

      return {
        key: periodConfig.key,
        title: periodConfig.title,
        hasData: !!topRecord,
        rankedVehicles: rankedVehicles.map((item) => ({
          vehicleId: item.vehicleId,
          vehicleLabel: this.vehicleLabel(item.vehicleId),
          distanceLabel: this.formatDistance(item.distance)
        }))
      }
    },
    async ensureVehicleLabels (vehicleIds) {
      const missingIds = vehicleIds.filter((id) => id && !this.vehicleNameById[id])
      if (!missingIds.length) {
        return
      }

      const profiles = await findTransportationProfiles({
        where: { id: { inq: missingIds } },
        fields: { id: true, licensePlateNumber: true }
      })

      for (let i = 0; i < profiles.length; i += 1) {
        const profile = profiles[i]
        if (profile && profile.id) {
          this.vehicleNameById[profile.id] = profile.licensePlateNumber || profile.id
        }
      }

      for (let j = 0; j < missingIds.length; j += 1) {
        const id = missingIds[j]
        if (!this.vehicleNameById[id]) {
          this.vehicleNameById[id] = 'Vehicle ' + String(id).slice(0, 8)
        }
      }
    },
    vehicleLabel (vehicleId) {
      return this.vehicleNameById[vehicleId] || ('Vehicle ' + String(vehicleId).slice(0, 8))
    },
    formatDistance (distanceValue) {
      return Number(distanceValue || 0).toFixed(3) + ' km'
    }
  }
}
